import React, { useState } from 'react'
import type { TeamDisciplineProfile, TeamDisciplineSplit } from '../../../types'
import { MinuteBandChart } from '../components/MinuteBandChart'
import { Sparkline } from '../../Sparkline'

interface TeamDisciplinePanelProps {
  homeTeam: TeamDisciplineProfile
  awayTeam: TeamDisciplineProfile
}

type DataWindow = 'season' | 'last_10' | 'last_5' | 'last_3'
type ContextSplit = 'all' | 'home_away' | 'game_state' | 'opponent_strength'

export function TeamDisciplinePanel({ homeTeam, awayTeam }: TeamDisciplinePanelProps) {
  const [dataWindow, setDataWindow] = useState<DataWindow>('season')
  const [contextSplit, setContextSplit] = useState<ContextSplit>('all')

  // Get stats for selected window
  const getStats = (team: TeamDisciplineProfile): TeamDisciplineSplit | null => {
    switch (dataWindow) {
      case 'season':
        return team.season
      case 'last_10':
        return team.last_10 || team.season
      case 'last_5':
        return team.last_5 || team.season
      case 'last_3':
        return team.last_3 || team.season
      default:
        return team.season
    }
  }

  // Get context-specific stats
  const getContextStats = (team: TeamDisciplineProfile, isHome: boolean): TeamDisciplineSplit | null => {
    switch (contextSplit) {
      case 'home_away':
        return isHome ? team.home_only || team.season : team.away_only || team.season
      case 'game_state':
        return team.when_leading || team.season // Could toggle between leading/trailing
      case 'opponent_strength':
        return team.vs_top_half || team.season // Could toggle between top/bottom
      default:
        return getStats(team)
    }
  }

  const homeStats = getContextStats(homeTeam, true)
  const awayStats = getContextStats(awayTeam, false)

  // Stat comparison helper
  const StatRow = ({ label, homeValue, awayValue, format = 'decimal', inverse = false }: {
    label: string
    homeValue: number | null | undefined
    awayValue: number | null | undefined
    format?: 'decimal' | 'percent' | 'integer'
    inverse?: boolean // If true, lower is better
  }) => {
    const formatValue = (val: number | null | undefined) => {
      if (val == null) return '-'
      switch (format) {
        case 'percent':
          return `${(val * 100).toFixed(0)}%`
        case 'integer':
          return val.toFixed(0)
        default:
          return val.toFixed(1)
      }
    }

    // Determine which team has advantage
    const getHighlightClass = (isHome: boolean) => {
      if (homeValue == null || awayValue == null) return ''
      const isHigher = isHome ? homeValue > awayValue : awayValue > homeValue
      const hasAdvantage = inverse ? !isHigher : isHigher
      return hasAdvantage ? 'highlighted' : ''
    }

    return (
      <div className="team-stat-row">
        <span className={`team-stat-value home ${getHighlightClass(true)}`}>
          {formatValue(homeValue)}
        </span>
        <span className="team-stat-label">{label}</span>
        <span className={`team-stat-value away ${getHighlightClass(false)}`}>
          {formatValue(awayValue)}
        </span>
      </div>
    )
  }

  return (
    <div className="cards-panel team-discipline-panel">
      {/* Team Headers */}
      <div className="team-headers">
        <div className="team-header home">
          {homeTeam.team_logo && (
            <img src={homeTeam.team_logo} alt="" className="team-header-logo" />
          )}
          <span className="team-header-name">{homeTeam.team_name}</span>
        </div>
        <div className="team-header away">
          {awayTeam.team_logo && (
            <img src={awayTeam.team_logo} alt="" className="team-header-logo" />
          )}
          <span className="team-header-name">{awayTeam.team_name}</span>
        </div>
      </div>

      {/* Data Window Selector */}
      <div className="cards-window-selector">
        <button
          type="button"
          className={`chip ${dataWindow === 'season' ? 'chip-active' : ''}`}
          onClick={() => setDataWindow('season')}
        >
          Season
        </button>
        <button
          type="button"
          className={`chip ${dataWindow === 'last_10' ? 'chip-active' : ''}`}
          onClick={() => setDataWindow('last_10')}
        >
          Last 10
        </button>
        <button
          type="button"
          className={`chip ${dataWindow === 'last_5' ? 'chip-active' : ''}`}
          onClick={() => setDataWindow('last_5')}
        >
          Last 5
        </button>
        <button
          type="button"
          className={`chip ${dataWindow === 'last_3' ? 'chip-active' : ''}`}
          onClick={() => setDataWindow('last_3')}
        >
          Last 3
        </button>
      </div>

      {/* Context Split Selector */}
      <div className="cards-window-selector cards-context-selector">
        <button
          type="button"
          className={`chip chip-small ${contextSplit === 'all' ? 'chip-active' : ''}`}
          onClick={() => setContextSplit('all')}
        >
          Overall
        </button>
        <button
          type="button"
          className={`chip chip-small ${contextSplit === 'home_away' ? 'chip-active' : ''}`}
          onClick={() => setContextSplit('home_away')}
        >
          Home/Away
        </button>
        {(homeTeam.when_leading || awayTeam.when_leading) && (
          <button
            type="button"
            className={`chip chip-small ${contextSplit === 'game_state' ? 'chip-active' : ''}`}
            onClick={() => setContextSplit('game_state')}
          >
            Game State
          </button>
        )}
        {(homeTeam.vs_top_half || awayTeam.vs_top_half) && (
          <button
            type="button"
            className={`chip chip-small ${contextSplit === 'opponent_strength' ? 'chip-active' : ''}`}
            onClick={() => setContextSplit('opponent_strength')}
          >
            vs Opponent
          </button>
        )}
      </div>

      {/* Stats Comparison */}
      <div className="team-stats-comparison">
        <StatRow
          label="Yellows For/Match"
          homeValue={homeStats?.yellows_for_per_match}
          awayValue={awayStats?.yellows_for_per_match}
        />
        <StatRow
          label="Yellows Against/Match"
          homeValue={homeStats?.yellows_against_per_match}
          awayValue={awayStats?.yellows_against_per_match}
          inverse
        />
        <StatRow
          label="Fouls Committed/Match"
          homeValue={homeStats?.fouls_committed_per_match}
          awayValue={awayStats?.fouls_committed_per_match}
        />
        <StatRow
          label="Fouls Suffered/Match"
          homeValue={homeStats?.fouls_suffered_per_match}
          awayValue={awayStats?.fouls_suffered_per_match}
          inverse
        />
        <StatRow
          label="Cards Per Foul"
          homeValue={homeStats?.cards_per_foul}
          awayValue={awayStats?.cards_per_foul}
        />
        <StatRow
          label="Matches"
          homeValue={homeStats?.matches}
          awayValue={awayStats?.matches}
          format="integer"
        />
      </div>

      {/* Sparklines */}
      <div className="team-sparklines">
        <div className="team-sparkline home">
          <span className="team-sparkline-label">Recent Cards</span>
          {homeTeam.recent_card_counts.length > 0 ? (
            <Sparkline
              values={homeTeam.recent_card_counts}
              width={100}
              height={30}
              color="var(--color-primary)"
            />
          ) : (
            <span className="team-sparkline-na">No data</span>
          )}
        </div>
        <div className="team-sparkline away">
          <span className="team-sparkline-label">Recent Cards</span>
          {awayTeam.recent_card_counts.length > 0 ? (
            <Sparkline
              values={awayTeam.recent_card_counts}
              width={100}
              height={30}
              color="var(--color-primary)"
            />
          ) : (
            <span className="team-sparkline-na">No data</span>
          )}
        </div>
      </div>

      {/* Streaks */}
      {(homeTeam.card_streak || homeTeam.clean_streak || awayTeam.card_streak || awayTeam.clean_streak) && (
        <div className="team-streaks">
          <div className="team-streak home">
            {homeTeam.card_streak && homeTeam.card_streak > 0 && (
              <span className="team-streak-badge streak-card">
                {homeTeam.card_streak} game card streak
              </span>
            )}
            {homeTeam.clean_streak && homeTeam.clean_streak > 0 && (
              <span className="team-streak-badge streak-clean">
                {homeTeam.clean_streak} games without card
              </span>
            )}
          </div>
          <div className="team-streak away">
            {awayTeam.card_streak && awayTeam.card_streak > 0 && (
              <span className="team-streak-badge streak-card">
                {awayTeam.card_streak} game card streak
              </span>
            )}
            {awayTeam.clean_streak && awayTeam.clean_streak > 0 && (
              <span className="team-streak-badge streak-clean">
                {awayTeam.clean_streak} games without card
              </span>
            )}
          </div>
        </div>
      )}

      {/* Minute Band Distribution */}
      {(homeTeam.cards_by_minute || awayTeam.cards_by_minute) && (
        <div className="team-minute-bands">
          <h4 className="cards-section-title">Card Timing Distribution</h4>
          <div className="team-minute-bands-grid">
            {homeTeam.cards_by_minute && (
              <div className="team-minute-band">
                <span className="team-minute-band-label">{homeTeam.team_name}</span>
                <MinuteBandChart bands={homeTeam.cards_by_minute} height={60} />
              </div>
            )}
            {awayTeam.cards_by_minute && (
              <div className="team-minute-band">
                <span className="team-minute-band-label">{awayTeam.team_name}</span>
                <MinuteBandChart bands={awayTeam.cards_by_minute} height={60} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
